/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 * Written by Hendrik, December 28, 2025.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from "./navbar.jsx";
import Footer from "./Footer.jsx";
import ResourceBank from './components/ResourceBank.jsx';
import Dashboard from './dashboard.jsx';
import Recent from './recents.jsx';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import Mainsubjects from './mainsubjects.jsx';
import Subjectdetail from './subjectdetail.jsx';
import SubjectNotes from './subjectnotes.jsx';
import Notes from './Notes.jsx';
import AllStudents from './allstudents.jsx';
import Login from './Login.jsx';
import UserManagement from './UserManagement.jsx';
import SchoolManagement from './SchoolManagement.jsx';
import HODDashboard from './HODDashboard.jsx';
import DeveloperDashboard from './DeveloperDashboard.jsx';
import PublicSubscription from './PublicSubscription.jsx';
import PrivacyPolicy from './PrivacyPolicy.jsx';
import Settings from './Settings.jsx';
import ExportMarks from './ExportMarks.jsx';
import FoundationPhaseAdmin from './FoundationPhaseAdmin.jsx';
import AITools from './AITools.jsx';
import LessonPlans from './LessonPlans.jsx';
import WorksheetGenerator from './WorksheetGenerator.jsx';
import AITestGenerator from './AITestGenerator.jsx';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const authStatus = localStorage.getItem('isAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      axios.get(`${process.env.REACT_APP_API_URL}/subscription_status.php`)
        .then(({ data }) => {
          setSubscriptionActive(Boolean(data.subscription_active));
          localStorage.setItem('expiry_date', data.expiry_date || '');
          if (data.user_id) localStorage.setItem('user_id', data.user_id);
        })
        .catch(() => {
          // Do not accidentally unlock access when status verification fails.
          setSubscriptionActive(false);
        })
        .finally(() => setSubscriptionChecked(true));
    } else {
      setSubscriptionChecked(true);
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setSubscriptionChecked(false);
    axios.get(`${process.env.REACT_APP_API_URL}/subscription_status.php`)
      .then(({ data }) => {
        setSubscriptionActive(Boolean(data.subscription_active));
        localStorage.setItem('expiry_date', data.expiry_date || '');
        if (data.user_id) localStorage.setItem('user_id', data.user_id);
      })
      .catch(() => setSubscriptionActive(false))
      .finally(() => setSubscriptionChecked(true));
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    localStorage.removeItem('is_admin');
    localStorage.removeItem('is_hod');
    localStorage.removeItem('user_role');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('expiry_date');
    setIsAuthenticated(false);
    setSubscriptionChecked(true);
  };

  if (loading || (isAuthenticated && !subscriptionChecked)) {
    return <div className="app-loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/subscription" element={<PublicSubscription />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Login remains available, but an expired account may only reach the
  // dashboard renewal screen. Route-level gating prevents URL bypasses.
  if (!subscriptionActive) {
    return (
      <BrowserRouter>
        <div className="app-shell">
          <Navbar onLogout={handleLogout} />
          <div className="app-main">
            <Routes>
              <Route path="/dashboard" element={<Dashboard total_students={20} />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </BrowserRouter>
    );
  }

  // Check if user is HOD
  const isHOD = localStorage.getItem('is_hod') === 'true';
  const isDeveloper = localStorage.getItem('user_role') === 'developer';

  // If HOD, show only analytics dashboard
  if (isHOD) {
    return (
      <BrowserRouter>
        <div className="app-shell">
          <Navbar onLogout={handleLogout} />
          <div className="app-main">
            <Routes>
              <Route path='/' element={<HODDashboard />} />
              <Route path='/analytics' element={<HODDashboard />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </BrowserRouter>
    );
  }

  // If Developer, show only developer dashboard
  if (isDeveloper) {
    return (
      <BrowserRouter>
        <div className="app-shell">
          <Navbar onLogout={handleLogout} />
          <div className="app-main">
            <Routes>
              <Route path='/' element={<DeveloperDashboard />} />
              <Route path='/developer' element={<DeveloperDashboard />} />
              <Route path='/schools' element={<SchoolManagement />} />
              <Route path='/foundation-phase' element={<FoundationPhaseAdmin />} />
              <Route path='/resources' element={<ResourceBank />} />
              <Route path='/ai-tools' element={<AITools />} />
              <Route path='/worksheets' element={<WorksheetGenerator />} />
              <Route path='/ai-tests' element={<AITestGenerator />} />
              <Route path='/lesson-plans' element={<LessonPlans />} />
              <Route path="*" element={<Navigate to="/developer" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div className="app-shell">
        <Navbar onLogout={handleLogout} />
        <div className="app-main">
          <Routes>
            <Route path='/' element={<div className="app-container">
              <Dashboard total_students={20} />
              <Recent />
            </div>}></Route>

            <Route path='/dashboard' element={<div className="app-container">
              <Dashboard total_students={20} />
              <Recent />
            </div>}></Route>

            <Route path='/subjects' element={<Mainsubjects />}></Route>

            <Route path='/subjects/:id' element={<Subjectdetail />}></Route>
            <Route path='/subjects/:id/notes' element={<SubjectNotes />}></Route>
            <Route path='/notes' element={<Notes />}></Route>

            <Route path='/students' element={<AllStudents />}></Route>

            <Route path='/users' element={
              localStorage.getItem('is_admin') === 'true'
                ? <UserManagement />
                : <Navigate to="/dashboard" replace />
            }></Route>

            <Route path='/foundation-phase' element={
              localStorage.getItem('is_admin') === 'true' || localStorage.getItem('user_role') === 'developer'
                ? <FoundationPhaseAdmin />
                : <Navigate to="/dashboard" replace />
            }></Route>

            <Route path='/schools' element={
              localStorage.getItem('user_role') === 'developer'
                ? <SchoolManagement />
                : <Navigate to="/dashboard" replace />
            }></Route>

            <Route path="/settings" element={<Settings />} />
            <Route path="/export-marks" element={<ExportMarks />} />
            <Route path="/ai-tools" element={<AITools />} />
            <Route path="/worksheets" element={<WorksheetGenerator />} />
            <Route path="/ai-tests" element={<AITestGenerator />} />
            <Route path="/lesson-plans" element={<LessonPlans />} />
            <Route path="/resources" element={<ResourceBank />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
